import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  deleteNocType,
  getNocTypes,
  saveNocType,
} from "../../../../store/actions/councilActions/nocAction";
import { errorAlert } from "../../../../utils/alert";

/*
  NOC Types, council side.

  The council decides which kinds of NOC are on offer. Each has a subject (the
  type / purpose), a form number and two fees - one for a 3-month validity and
  one for a 6-month validity. Applicants pick from this list; the subject is
  never typed by the applicant.
*/
const EMPTY = {
  subject: "",
  form_no: "",
  fee_3m: 0,
  fee_6m: 0,
  sort_order: 0,
  is_active: 1,
};

const NocTypeList = () => {
  const dispatch = useDispatch();

  const [list, setList] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setList(null);
    const rows = await dispatch(getNocTypes());
    setList(rows);
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) return errorAlert("Please write the NOC subject / type.");
    if (!form.form_no.trim()) return errorAlert("Please write the form number.");

    setBusy(true);
    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      body.append(key, value === null ? "" : value);
    });

    const done = await dispatch(saveNocType(body));
    setBusy(false);
    if (done) {
      setForm(null);
      load();
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.subject}"?`)) return;
    if (await dispatch(deleteNocType(row.id))) load();
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <div className="BigTitle">NOC Types</div>
        <button
          type="button"
          className="btn btn-comman"
          onClick={() => setForm({ ...EMPTY })}
        >
          Add NOC Type
        </button>
      </div>

      <div className="table-responsive mt-3">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: "70px" }}>Order</th>
              <th>Subject (Type)</th>
              <th>Form No.</th>
              <th>03 Months Fee</th>
              <th>06 Months Fee</th>
              <th>Status</th>
              <th style={{ width: "140px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {list === null ? (
              <tr>
                <td colSpan={7}>Loading...</td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={7}>No NOC type added yet.</td>
              </tr>
            ) : (
              list.map((row) => (
                <tr key={row.id}>
                  <td>{row.sort_order}</td>
                  <td>{row.subject}</td>
                  <td>{row.form_no}</td>
                  <td>₹{Number(row.fee_3m)}</td>
                  <td>₹{Number(row.fee_6m)}</td>
                  <td>
                    {Number(row.is_active) === 1 ? (
                      <span className="text-success">Live</span>
                    ) : (
                      <span className="text-muted">Hidden</span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-light-primary mr-1"
                      onClick={() =>
                        setForm({
                          id: row.id,
                          subject: row.subject || "",
                          form_no: row.form_no || "",
                          fee_3m: Number(row.fee_3m) || 0,
                          fee_6m: Number(row.fee_6m) || 0,
                          sort_order: Number(row.sort_order) || 0,
                          is_active: Number(row.is_active),
                        })
                      }
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => remove(row)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal show={!!form} onHide={() => setForm(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{form?.id ? "Edit NOC Type" : "Add NOC Type"}</Modal.Title>
        </Modal.Header>
        {form && (
          <form onSubmit={submit}>
            <Modal.Body>
              <div className="row">
                <div className="col-md-8 mb-2">
                  <label>Subject (Type of NOC) *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.subject}
                    placeholder="e.g. Migration NOC"
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <label>Form No. *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.form_no}
                    placeholder="e.g. NSVEC/NOC/CF-001"
                    onChange={(e) => setForm({ ...form, form_no: e.target.value })}
                  />
                </div>
                <div className="col-md-3 mb-2">
                  <label>03 Months Fee (₹) *</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="form-control"
                    value={form.fee_3m}
                    onChange={(e) => setForm({ ...form, fee_3m: e.target.value })}
                  />
                </div>
                <div className="col-md-3 mb-2">
                  <label>06 Months Fee (₹) *</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="form-control"
                    value={form.fee_6m}
                    onChange={(e) => setForm({ ...form, fee_6m: e.target.value })}
                  />
                </div>
                <div className="col-md-3 mb-2">
                  <label>Order</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  />
                </div>
                <div className="col-md-3 mb-2">
                  <label>Status</label>
                  <select
                    className="form-control"
                    value={form.is_active}
                    onChange={(e) =>
                      setForm({ ...form, is_active: Number(e.target.value) })
                    }
                  >
                    <option value={1}>Live</option>
                    <option value={0}>Hidden</option>
                  </select>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setForm(null)}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-comman" disabled={busy}>
                {busy ? "Saving..." : "Save"}
              </button>
            </Modal.Footer>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default NocTypeList;
